import { Bundle, MeasureReport } from "@medplum/fhirtypes";
import { executeWithNetworkRetries } from "@metriport/shared";
import axios from "axios";
import { z } from "zod";
import type { PaginationMetadata } from "../types/pagination-metadata";
import { apiOSS } from "./apiOSS";
import { getSandboxCareGaps } from "./care-gaps-mock";
import {
  OSS_BUNDLE_PAGE_SIZE,
  fetchAllBundlePages,
  type BundlesPageResponse,
} from "./fetch-oss-paginated-bundles";

const bundleSchema = z.object({
  resourceType: z.literal("Bundle"),
  entry: z
    .array(
      z.object({
        resource: z.record(z.unknown()),
      })
    )
    .optional(),
});

const paginationMetadataSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

const careGapsResponseSchema = z.object({
  metadata: paginationMetadataSchema,
  results: z.array(bundleSchema),
});

type CareGapsResponse = {
  metadata: PaginationMetadata;
  results: Bundle[];
};

function findMeasureReport(bundle: Bundle): MeasureReport | undefined {
  return bundle.entry?.find(e => e.resource?.resourceType === "MeasureReport")?.resource as
    | MeasureReport
    | undefined;
}

/**
 * Exclude a care-gap bundle if any group has initial-population or denominator count 0,
 * or denominator-exclusion count exactly 1.
 */
export function isCareGapBundleUseless(bundle: Bundle): boolean {
  const measureReport = findMeasureReport(bundle);
  if (!measureReport?.group || measureReport.group.length === 0) {
    return false;
  }

  for (const group of measureReport.group) {
    let initialPopulation = 0;
    let denominatorExclusion = 0;
    let denominator = 0;

    for (const pop of group.population ?? []) {
      const popCode = pop.code?.coding?.[0]?.code?.toLowerCase() ?? "";
      const count = pop.count ?? 0;

      if (popCode === "initial-population") {
        initialPopulation = count;
      } else if (popCode === "denominator-exclusion") {
        denominatorExclusion = count;
      } else if (popCode === "denominator") {
        denominator = count;
      }
    }

    if (initialPopulation === 0 || denominator === 0 || denominatorExclusion === 1) {
      return true;
    }
  }

  return false;
}

export function dropUselessCareGapBundles(bundles: Bundle[]): Bundle[] {
  return bundles.filter(bundle => !isCareGapBundleUseless(bundle));
}

async function fetchCareGapsPage(
  patientId: string,
  limit: number,
  offset: number
): Promise<BundlesPageResponse> {
  try {
    const resp = await executeWithNetworkRetries(
      () =>
        apiOSS.get(`/medical/v1/patient/${encodeURIComponent(patientId)}/care-gaps`, {
          params: { limit, offset },
        }),
      { maxAttempts: 5, initialDelay: 100 }
    );
    const parsed = careGapsResponseSchema.parse(resp.data);
    return parsed as BundlesPageResponse;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return {
        metadata: { total: 0, limit, offset },
        results: [],
      };
    }
    throw error;
  }
}

export async function getCareGaps(
  patientId: string,
  isUserInSandbox = false
): Promise<CareGapsResponse> {
  if (isUserInSandbox) {
    const sandboxResult = await getSandboxCareGaps(patientId);
    const filtered = dropUselessCareGapBundles(sandboxResult.results);
    return {
      ...sandboxResult,
      metadata: { ...sandboxResult.metadata, total: filtered.length },
      results: filtered,
    };
  }
  const { results: allBundles } = await fetchAllBundlePages((limit, offset) =>
    fetchCareGapsPage(patientId, limit, offset)
  );
  const filtered = dropUselessCareGapBundles(allBundles);
  return {
    metadata: {
      total: filtered.length,
      limit: OSS_BUNDLE_PAGE_SIZE,
      offset: 0,
    },
    results: filtered,
  };
}
